import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';

const metricLabels={prospect_contacted:'Outbound DMs',response:'Responses',conversation_started:'Conversations'};
const actionLabels={edit:'Manual edit',undo:'Undo',redo:'Redo'};
const methodLabels={autosave:'click-outside autosave',enter:'Enter to save',save_changes:'Save Changes',navigation:'Save & continue',keyboard:'Keyboard action',today_counter:'Today counter'};
const nyDate=value=>new Intl.DateTimeFormat('en-US',{timeZone:'America/New_York',month:'short',day:'numeric'}).format(new Date(`${value}T12:00:00-04:00`));
const nyDateFromTimestamp=value=>new Intl.DateTimeFormat('en-US',{timeZone:'America/New_York',month:'short',day:'numeric'}).format(new Date(value));
const nyDateTime=value=>new Intl.DateTimeFormat('en-US',{timeZone:'America/New_York',month:'short',day:'numeric',hour:'numeric',minute:'2-digit'}).format(new Date(value));
const nyTime=value=>new Intl.DateTimeFormat('en-US',{timeZone:'America/New_York',hour:'numeric',minute:'2-digit'}).format(new Date(value));
const nyDayKey=value=>new Intl.DateTimeFormat('en-CA',{timeZone:'America/New_York',year:'numeric',month:'2-digit',day:'2-digit'}).format(new Date(value));
const todayKey=()=>new Intl.DateTimeFormat('en-CA',{timeZone:'America/New_York',year:'numeric',month:'2-digit',day:'2-digit'}).format(new Date());

function actorLabel(event,userId,profiles){
  if(event.actor_id===userId)return 'You';
  const profile=profiles[event.actor_id];
  return profile?.display_name||profile?.email||'Team member';
}

function eventRecordedAt(event){return event.metadata?.recorded_at||event.created_at;}
function eventBusinessDate(event){return event.metadata?.business_date||nyDayKey(event.occurred_at||event.created_at);}
function eventSource(event){return event.metadata?.source||'';}
function isHistoryEvent(event){return eventSource(event)==='activity_inline_edit'||eventSource(event)==='today_counter';}

function reconstructLegacyTodayTotals(rows){
  const totals=new Map();
  return [...rows].sort((a,b)=>new Date(a.created_at)-new Date(b.created_at)).map(event=>{
    const key=`${eventBusinessDate(event)}:${event.event_type}`;
    const previous=Number(totals.get(key)||0);
    const corrected=previous+Number(event.numeric_value||0);
    totals.set(key,corrected);
    const metadata=event.metadata||{};
    if(eventSource(event)==='today_counter'&&(metadata.previous_total===undefined||metadata.corrected_total===undefined)){
      return {...event,metadata:{...metadata,previous_total:previous,corrected_total:corrected,legacy_reconstructed:true}};
    }
    return event;
  });
}

export default function ActivityEditHistoryDrawer({open,onClose,workspaceId,userId}){
  const [events,setEvents]=useState([]),[profiles,setProfiles]=useState({}),[loading,setLoading]=useState(false),[error,setError]=useState('');
  const [metric,setMetric]=useState('all'),[changeType,setChangeType]=useState('all');

  useEffect(()=>{
    if(!open||!workspaceId)return undefined;
    let cancelled=false;
    async function load(){
      setLoading(true);setError('');
      const {data,error:historyError}=await supabase.from('activity_events')
        .select('id,event_type,actor_id,numeric_value,occurred_at,metadata,created_at')
        .eq('workspace_id',workspaceId)
        .eq('motion','outbound')
        .in('event_type',['prospect_contacted','response','conversation_started'])
        .order('created_at',{ascending:false})
        .limit(500);
      if(cancelled)return;
      if(historyError){setError(historyError.message);setEvents([]);setProfiles({});setLoading(false);return;}
      const reconstructed=reconstructLegacyTodayTotals(data||[]);
      const rows=reconstructed.filter(isHistoryEvent);
      setEvents(rows);
      const actorIds=[...new Set(rows.map(row=>row.actor_id).filter(Boolean))];
      if(actorIds.length){
        const {data:profileRows,error:profileError}=await supabase.from('profiles').select('id,display_name,email').in('id',actorIds);
        if(!cancelled&&!profileError)setProfiles(Object.fromEntries((profileRows||[]).map(profile=>[profile.id,profile])));
      }
      if(!cancelled)setLoading(false);
    }
    load();
    return ()=>{cancelled=true};
  },[open,workspaceId]);

  useEffect(()=>{
    if(!open)return undefined;
    const onKeyDown=event=>{if(event.key==='Escape')onClose();};
    document.addEventListener('keydown',onKeyDown);
    return ()=>document.removeEventListener('keydown',onKeyDown);
  },[open,onClose]);

  const filtered=useMemo(()=>events.filter(event=>{
    const action=event.metadata?.action_kind||'edit';
    return (metric==='all'||event.event_type===metric)&&(changeType==='all'||action===changeType);
  }),[events,metric,changeType]);

  const groups=useMemo(()=>{
    const sessions=new Map();
    for(const event of filtered){
      const metadata=event.metadata||{};
      const source=eventSource(event);
      const sessionKey=source==='today_counter'&&!metadata.edit_session_id?`today:${event.id}`:(metadata.edit_session_id||metadata.action_group_id||event.id);
      if(!sessions.has(sessionKey))sessions.set(sessionKey,[]);
      sessions.get(sessionKey).push(event);
    }
    const sorted=[...sessions.values()].map(session=>session.sort((a,b)=>new Date(eventRecordedAt(a))-new Date(eventRecordedAt(b)))).sort((a,b)=>new Date(eventRecordedAt(b[b.length-1]))-new Date(eventRecordedAt(a[a.length-1])));
    const byDay=[];
    for(const session of sorted){
      const latest=session[session.length-1];
      const day=nyDayKey(eventRecordedAt(latest));
      let group=byDay.find(item=>item.day===day);
      if(!group){group={day,sessions:[]};byDay.push(group);}
      group.sessions.push(session);
    }
    return byDay;
  },[filtered]);

  const renderDay=day=>{
    const today=todayKey();
    if(day===today)return 'Today';
    const yesterdayDate=new Date();yesterdayDate.setDate(yesterdayDate.getDate()-1);
    const yesterday=new Intl.DateTimeFormat('en-CA',{timeZone:'America/New_York',year:'numeric',month:'2-digit',day:'2-digit'}).format(yesterdayDate);
    if(day===yesterday)return 'Yesterday';
    return new Intl.DateTimeFormat('en-US',{timeZone:'America/New_York',month:'short',day:'numeric',year:'numeric'}).format(new Date(`${day}T12:00:00-04:00`));
  };

  return <>
    <div className={`activity-history-backdrop${open?' show':''}`} onMouseDown={onClose} aria-hidden="true" />
    <aside className={`activity-history-drawer${open?' show':''}`} aria-hidden={!open} aria-label="Activity Edit History">
      <div className="activity-history-head"><div><h2>Activity Edit History</h2><p>Permanent, read-only record of committed Activity corrections and Today counter changes.</p></div><button className="activity-history-close" aria-label="Close edit history" onClick={onClose}>×</button></div>
      <div className="activity-history-filters"><select value={metric} onChange={e=>setMetric(e.target.value)}><option value="all">All metrics</option><option value="prospect_contacted">Outbound DMs</option><option value="response">Responses</option><option value="conversation_started">Conversations</option></select><select value={changeType} onChange={e=>setChangeType(e.target.value)}><option value="all">All change types</option><option value="edit">Manual edits</option><option value="undo">Undo</option><option value="redo">Redo</option></select></div>
      <div className="activity-history-list">
        {loading&&<div className="activity-history-empty">Loading edit history…</div>}
        {!loading&&error&&<div className="activity-history-error">{error}</div>}
        {!loading&&!error&&groups.length===0&&<div className="activity-history-empty">No Activity edits match these filters.</div>}
        {!loading&&!error&&groups.map(group=><div key={group.day} className="activity-history-day-group"><div className="activity-history-day">{renderDay(group.day)}</div>{group.sessions.map(session=>{
          const first=session[0],last=session[session.length-1];
          const firstRecorded=eventRecordedAt(first),lastRecorded=eventRecordedAt(last);
          const todayCounter=eventSource(first)==='today_counter';
          return <div className="activity-history-session" key={first.metadata?.edit_session_id||first.metadata?.action_group_id||first.id}>
            <div className="activity-history-session-head"><div><strong>{todayCounter?'Today counter':'Edit session'} · {session.length} {session.length===1?'change':'changes'}</strong><span>{nyDateTime(firstRecorded)}{session.length>1?`–${nyTime(lastRecorded)}`:''} · {actorLabel(first,userId,profiles)}</span></div><span>Daily · Outbound</span></div>
            {session.map(event=>{
              const metadata=event.metadata||{},recorded=eventRecordedAt(event),action=metadata.action_kind||'edit';
              const businessDate=eventBusinessDate(event);
              const hasTotals=metadata.previous_total!==undefined&&metadata.corrected_total!==undefined;
              return <div className="activity-history-entry" key={event.id}><div className="activity-history-entry-top"><strong>{metadata.business_date?nyDate(businessDate):nyDateFromTimestamp(event.occurred_at||event.created_at)} · {metricLabels[event.event_type]||event.event_type}</strong><span>{nyTime(recorded)}</span></div>{hasTotals&&<div className="activity-history-change"><span className="activity-history-old">{metadata.previous_total}</span><span>→</span><span className="activity-history-new">{metadata.corrected_total}</span></div>}<div className="activity-history-meta">{eventSource(event)==='today_counter'?'Today counter change':(actionLabels[action]||'Manual edit')}{metadata.save_method?` · ${methodLabels[metadata.save_method]||metadata.save_method}`:eventSource(event)==='today_counter'&&metadata.legacy_reconstructed?' · legacy entry':''}</div></div>;
            })}
          </div>;
        })}</div>)}
      </div>
      <div className="activity-history-foot">History remains available after Edit Mode ends. Undo/Redo remains session-scoped.</div>
    </aside>
  </>;
}
